import type { PaletteOptions } from '@mui/material/styles';

// Warm, quiet palette: one blue for actions and the assistant, warm greys for structure, white for content.
// Forecast-direction colours live in `market`; surface tokens are the only greys components use.
const BLUE = { main: '#2563EB', light: '#3B82F6', dark: '#1D4ED8', contrastText: '#FFFFFF' };

export const lightPalette: PaletteOptions = {
  mode: 'light',
  primary: BLUE,
  secondary: BLUE,
  background: { default: '#F3F2EE', paper: '#FFFFFF' },
  text: { primary: '#1A1A19', secondary: '#6B6A65', disabled: '#A3A19A' },
  divider: '#E6E4DE',
  success: { main: '#16A34A', dark: '#15803D' },
  warning: { main: '#B45309', dark: '#92400E' },
  error: { main: '#DC2626', dark: '#B91C1C' },
  info: { main: '#2563EB', dark: '#1D4ED8' },
  market: { up: '#15803D', down: '#B91C1C', flat: '#6B6A65' },
  surface: {
    subtle: '#F3F2EE',
    sunken: '#ECEAE5',
    highlight: '#FFF6E5',
    border: '#DAD7D0',
    borderStrong: '#B9B5AC',
    sidebar: '#FAFAF8',
    accentSoft: '#E8EFFD',
    avatar: '#FBE3D6',
    avatarText: '#B4532A',
  },
  // Soft background and ink pairs for analyst avatars, picked by name so a person keeps their colour.
  tints: [
    { bg: '#FBE3D6', fg: '#B4532A' },
    { bg: '#FBF0C9', fg: '#8A6A12' },
    { bg: '#DDF3E4', fg: '#2F7A4B' },
    { bg: '#E1ECFD', fg: '#2558C9' },
    { bg: '#ECE5FB', fg: '#6A4BB5' },
    { bg: '#EEEAE2', fg: '#6B6A65' },
  ],
  // One dot colour per asset class, so a report's kind reads at a glance.
  assetDot: { FX: '#2563EB', Rates: '#16A34A', Macro: '#8A5A3B', Credit: '#DD5B26', Commodities: '#D99A1E', Equity: '#7C3AED', ESG: '#0D9488' },
};

type Surface = {
  subtle: string;
  sunken: string;
  highlight: string;
  border: string;
  borderStrong: string;
  sidebar: string;
  /** Tinted blue behind badges, citations and the selected prompt filter. */
  accentSoft: string;
  avatar: string;
  avatarText: string;
};

type Tint = { bg: string; fg: string };

declare module '@mui/material/styles' {
  interface Palette {
    market: { up: string; down: string; flat: string };
    surface: Surface;
    tints: Tint[];
    assetDot: Record<string, string>;
  }
  interface PaletteOptions {
    market?: { up: string; down: string; flat: string };
    surface?: Surface;
    tints?: Tint[];
    assetDot?: Record<string, string>;
  }
}
